/**
 * Token Refresh Manager - Proactive token renewal
 * 
 * This service listens to Firebase auth state changes and handles
 * proactive token refresh before expiration. Firebase ID tokens
 * expire after 1 hour, so we refresh ~5 minutes before expiry.
 * 
 * Firebase SDK automatically handles refresh token exchange internally.
 */

import { auth } from '../firebase';

let tokenRefreshTimer = null;
let lastTokenRefresh = null;

/**
 * Initialize proactive token refresh
 * Listens for auth state changes and sets up refresh timers
 */
export function initializeAuthService() {
  // Listen for auth state changes (login/logout/token refresh)
  auth.onAuthStateChanged(async (user) => {
    // Clear any existing timer
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }

    if (user) {
      // Schedule first refresh check
      scheduleTokenRefresh(user);
    }
  });

  // Also listen for explicit token changes (Firebase auto-refreshes periodically)
  const unsubscribe = auth.onIdTokenChanged((user) => {
    if (user) {
      lastTokenRefresh = new Date();
      
      // Reschedule refresh timer with new token
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
      }
      scheduleTokenRefresh(user);
    }
  });

  return unsubscribe; // Return unsubscribe function for cleanup if needed
}

/**
 * Schedule token refresh ~5 minutes before expiration
 * @param {FirebaseUser} user - Current authenticated user
 */
async function scheduleTokenRefresh(user) {
  try {
    // Get current token with full claims (including expiration)
    const token = await user.getIdToken(false); // false = don't force refresh yet
    
    if (!token) {
      return;
    }

    // Decode token to get expiration time
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return;
    }

    // Decode payload (base64url decode)
    const payload = JSON.parse(atob(parts[1]));
    const expiresAt = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    // Refresh 5 minutes before expiry (300,000ms)
    const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);

    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
    }

    tokenRefreshTimer = setTimeout(async () => {
      try {
        // Force refresh token before it expires
        await user.getIdToken(true); // true = force refresh
        lastTokenRefresh = new Date();

        // Reschedule next refresh
        scheduleTokenRefresh(user);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Token refresh failed:', error);
        }
        // If refresh fails, user will get 401 on next API call and reactive handler will logout
      }
    }, refreshTime);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error scheduling token refresh:', error);
    }
  }
}

/**
 * Get last token refresh timestamp (for debugging/monitoring)
 * @returns {Date|null} Last refresh time or null if never refreshed
 */
export function getLastTokenRefreshTime() {
  return lastTokenRefresh;
}

/**
 * Force immediate token refresh (useful for critical operations)
 * @returns {Promise<string>} New token
 */
export async function forceTokenRefresh() {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    const token = await auth.currentUser.getIdToken(true); // true = force refresh
    lastTokenRefresh = new Date();
    
    return token;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Failed to force token refresh:', error);
    }
    throw error;
  }
}

/**
 * Clear refresh timer (called on logout)
 */
export function clearRefreshTimer() {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}
